import React, { useRef } from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  TimeSeriesScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js';
import 'chartjs-adapter-moment';
import { Line } from 'react-chartjs-2';
import { formatCurrency, toQuarter, toYear } from '../../helpers/format';
import useIncomeStatements from '../../hooks/useIncomeStatements';
import style from './StockDetails.module.css';

const IncomeStatementChart = ({ stock, period }) => {
  const { incomeStatements } = useIncomeStatements(stock.id, period);

  ChartJS.register(
    LinearScale,
    TimeSeriesScale,
    PointElement,
    LineElement,
    Tooltip,
    Filler,
    Legend
  );

  const chartRef = useRef(null);

  if (!incomeStatements || !incomeStatements.history || incomeStatements.history.length === 0) return null;

  return <>
    <Line options={chartOptions(period)} data={data(incomeStatements, period)} ref={chartRef} />
    <p className={style.chartLabelEstimate}>
      <span></span>
      Avg Analyst Estimate
    </p>
  </>;
};

const COLOR_REVENUE = [0, 95, 115];
const COLOR_INCOME = [148, 210, 189];

function data(incomeStatements, period) {
  const [labels, revenue, netIncome] = formatData(incomeStatements.history);

  incomeStatements.estimates && incomeStatements.estimates.forEach((estimate) => {
    const date = new Date(estimate.date);
    if (period === 'annual') {
      date.setMonth(0);
    }
    labels.unshift(date);
    revenue.unshift(estimate.estimated_revenue_avg);
    netIncome.unshift(estimate.estimated_net_income_avg);
  });

  const numberOfEstimates = incomeStatements.estimates.length || 0;

  return {
    labels: labels,
    datasets: [
      {
        label: 'Revenue',
        data: revenue,
        borderColor: `rgb(${COLOR_REVENUE.join(',')})`,
        borderWidth: 3,
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          let grad = ctx.createLinearGradient(0, 0, 0, 360);
          grad.addColorStop(0, `rgba(${COLOR_REVENUE.join(',')}, 0.05)`);
          grad.addColorStop(1, `rgba(${COLOR_REVENUE.join(',')}, 0)`);
          return grad;
        },
        segment: {
          borderDash: ctx => ctx.p0DataIndex < numberOfEstimates ? [4, 4] : undefined,
        },
      },
      {
        label: 'Net Income',
        data: netIncome,
        borderColor: `rgb(${COLOR_INCOME.join(',')})`,
        borderWidth: 3,
        fill: true,
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          let grad = ctx.createLinearGradient(0, 0, 0, 360);
          grad.addColorStop(0, `rgba(${COLOR_INCOME.join(',')}, 0.05)`);
          grad.addColorStop(1, `rgba(${COLOR_INCOME.join(',')}, 0)`);
          return grad;
        },
        segment: {
          borderDash: ctx => ctx.p0DataIndex < numberOfEstimates ? [4, 4] : undefined,
        },
      },
    ],
  };
}

export default IncomeStatementChart;

const formatData = (incomeStatements) => {
  let labels = [];
  let revenue = [];
  let netIncome = [];

  for (let i = 0; i < incomeStatements.length; i++) {
    const date = new Date(incomeStatements[i].date);
    labels.push(date);
    revenue.push(incomeStatements[i].total_revenue);
    netIncome.push(incomeStatements[i].net_income);
  }

  return [labels, revenue, netIncome];
};

function currency(value) {
  return `${value < 0 ? '-' : ''}$${formatCurrency(Math.abs(value), { compact: true })}`;
}

function chartOptions(period) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    pointStyle: false,
    animation: {
      duration: 0,
    },
    elements: {
      line: {
        tension: 0.1
      }
    },
    scales: {
      x: {
        type: 'timeseries',
        time: {
          tooltipFormat: 'MMM DD, YYYY',
          displayFormats: {
            month: 'MMM'
          },
          minUnit: 'quarter',
        },
        ticks: {
          maxRotation: 0,
          major: {
            enabled: true
          },
          font: function (context) {
            return context.tick && context.tick.major ? { weight: '700' } : undefined;
          },
        },
        grid: {
          display: false
        },
        border: {
          display: false,
        },
      },
      y: {
        grid: {
          display: false
        },
        border: {
          display: false,
        },
        ticks: {
          callback: (value) => currency(value),
        },
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyleWidth: 10,
          pointStyle: 'line',
        },
      },
      tooltip: {
        mode: 'index',
        position: 'nearest',
        intersect: false,
        callbacks: {
          title: (context) => {
            return period === 'quarterly' ? toQuarter(context[0].label) : toYear(context[0].label);
          },
          label: (context) => {
            return `${context.dataset.label}: ${currency(context.raw)}`;
          },
          labelPointStyle: () => 'circle',
          footer: function (values) {
            if (values.length < 2) {
              return;
            }
            let margin = Math.round((values[1].raw / values[0].raw) * 1000) / 10;
            return `Profit Margin: ${margin}%`;
          },
        },
        usePointStyle: true
      }
    }
  };
}